import { FC, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Stack } from '@mui/material';
import WavesurferPlayer from '@wavesurfer/react';
import WaveSurfer from 'wavesurfer.js';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import {
  AudioIconBox,
  AudioWrapper,
  ControlsWrapper,
  Marker,
  ProgressBar,
  ProgressBarContainer,
  TimeText,
} from './styles';
import useAudioStore from '@/stores/audioStore';
import VolumeControl from '../VolumeControl/VolumeControl';
import { DocumentChapter } from '@/types/note';
import { parseTime } from '@/helpers/formatTime';
import audioThumbnail from '@/assets/audio/audio_thumbnail.jpg';

interface AudioPlayerProps {
  src: string;
  chapters?: DocumentChapter[];
  audioReady?: boolean;
  isProcessing?: boolean;
}

const AudioPlayer: FC<AudioPlayerProps> = ({ src, chapters, audioReady = true, isProcessing = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const animationRef = useRef<number>(0);
  const progressRef = useRef<HTMLInputElement>(null);
  const {
    wavesurfer,
    setWavesurfer,
    currentTime,
    remainingTime,
    setProgressRef,
    setProgressTime,
    setDuration,
    currentWidth,
    setCurrentWidth,
    setWavesurferCurrentTime,
    setProgressCurrentTime,
    duration,
  } = useAudioStore();
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  const onReady = (ws: WaveSurfer) => {
    setWavesurfer(ws);
    setIsPlaying(false);
    setProgressRef(progressRef);
    const seconds = ws.getDuration();
    setDuration(seconds);
    if (progressRef.current) {
      progressRef.current.max = seconds.toString();
    }
    setProgressTime();
  };

  const onPlayPause = () => {
    if (wavesurfer) wavesurfer.playPause();
  };

  const changePlayerCurrentTime = () => {
    setCurrentWidth();
  };

  const whilePlaying = () => {
    if (progressRef.current && wavesurfer) {
      setProgressTime();

      progressRef.current.value = wavesurfer.getCurrentTime().toString();
      changePlayerCurrentTime();
      animationRef.current = requestAnimationFrame(whilePlaying);
    }
  };

  const onPlay = () => {
    setIsPlaying(true);
    whilePlaying();
  };

  const onPause = () => {
    setIsPlaying(false);
    cancelAnimationFrame(animationRef.current);
  };

  const changeRange = () => {
    if (progressRef.current && wavesurfer) {
      setWavesurferCurrentTime();
      changePlayerCurrentTime();
      if (!wavesurfer.isPlaying()) {
        setProgressTime();
      }
    }
  };

  const handleChapterClick = (time: number) => {
    setProgressCurrentTime(time);
    setWavesurferCurrentTime();
    setProgressTime();
  };

  const adjustMarkerPosition = (time: number, duration: number) => {
    const normalized = time / duration;
    const midPoint = 0.5;
    const scale = 0.99;
    return midPoint + (normalized - midPoint) * scale;
  };

  const handleVolumeUpdate = (newVolume: number) => {
    if (wavesurfer) {
      wavesurfer.setVolume(newVolume);
      setVolume(newVolume);
    }
  };

  const handleMuteToggle = (muted: boolean) => {
    if (wavesurfer) {
      wavesurfer.setMuted(muted);
      setIsMuted(muted);
    }
  };

  useEffect(() => {
    if (wavesurfer && !wavesurfer.isPlaying()) {
      wavesurfer.on('click', () => {
        setProgressTime();
      });
    }
  }, [wavesurfer, wavesurfer?.isPlaying()]);

  return (
    <Stack>
      {(!audioReady || isProcessing) && (
        <Box
          onClick={onPlayPause}
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            borderRadius: '22px',
            overflow: 'hidden',
            backgroundImage: `url(${audioThumbnail})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            cursor: 'pointer',
          }}
        >
          <AudioIconBox>
            <IconButton sx={{ color: 'white', fontSize: 48 }} disableRipple>
              {isPlaying ? <PauseRoundedIcon fontSize="inherit" /> : <PlayArrowRoundedIcon fontSize="inherit" />}
            </IconButton>
          </AudioIconBox>
        </Box>
      )}
      <AudioWrapper sx={{ position: 'relative', display: audioReady && !isProcessing ? 'block' : 'none' }}>
        <WavesurferPlayer
          height={50}
          waveColor="white"
          url={src}
          onReady={onReady}
          onPlay={onPlay}
          onPause={onPause}
          barWidth={2}
          barGap={2}
          barRadius={10}
          normalize={true}
          cursorWidth={0}
        />
      </AudioWrapper>

      {wavesurfer && (
        <ControlsWrapper sx={{ display: isProcessing ? 'none' : 'flex' }}>
          {isPlaying ? (
            <PauseRoundedIcon cursor="pointer" onClick={onPlayPause} />
          ) : (
            <PlayArrowRoundedIcon cursor="pointer" onClick={onPlayPause} />
          )}
          <TimeText variant="caption" width="40px">
            {currentTime}
          </TimeText>
          <ProgressBarContainer>
            <ProgressBar
              type="range"
              step="any"
              ref={progressRef}
              onChange={changeRange}
              value={wavesurfer?.getCurrentTime() || 0}
              min="0"
              currentWidth={currentWidth}
            />
            {chapters?.map((chapter, index) => {
              const chapterTime = parseTime(chapter.timecode);
              return (
                <Marker
                  key={`${index}-${chapterTime}`}
                  style={{ left: `${adjustMarkerPosition(chapterTime, duration) * 100}%` }}
                  onClick={() => handleChapterClick(chapterTime)}
                />
              );
            })}
          </ProgressBarContainer>
          <TimeText variant="caption" width="46px">
            {remainingTime}
          </TimeText>
          <VolumeControl
            volume={volume}
            isMuted={isMuted}
            onMuteToggle={handleMuteToggle}
            onVolumeChange={handleVolumeUpdate}
          />
        </ControlsWrapper>
      )}
    </Stack>
  );
};

export default AudioPlayer;
